import { useCallback, useDeferredValue, useEffect, useRef, useState } from "react";
import { tkadlecApi } from "../../services/tkadlecApi";

export default function TkadlecMemories() {
  const [memories, setMemories] = useState([]);
  const [memoryJobs, setMemoryJobs] = useState([]);
  const [memoryUsage, setMemoryUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  const [totalMatched, setTotalMatched] = useState(0);
  const [memoryStatus, setMemoryStatus] = useState("active");
  const [pageCursors, setPageCursors] = useState([""]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMemoryId, setSelectedMemoryId] = useState(null);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationMessage, setMutationMessage] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const deferredSearch = useDeferredValue(searchQuery.trim());
  const requestRef = useRef(0);
  const detailRequestRef = useRef(0);

  const fetchMemoryUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      setMemoryUsage(await tkadlecApi.getMemoryUsage());
    } catch (requestError) {
      setUsageError(requestError.message);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  const fetchMemories = useCallback(async (cursor = "") => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const [memoryData, jobData] = await Promise.all([
        tkadlecApi.getMemories({ status: memoryStatus, cursor, search: deferredSearch }),
        tkadlecApi.getMemoryJobs(),
      ]);
      if (requestId !== requestRef.current) return;
      setMemories(Array.isArray(memoryData.items) ? memoryData.items : []);
      setTotalMatched(Number(memoryData.totalMatched || 0));
      setNextCursor(memoryData.nextCursor || null);
      setMemoryJobs(Array.isArray(jobData.items) ? jobData.items : []);
      return Array.isArray(memoryData.items) ? memoryData.items : [];
    } catch (requestError) {
      if (requestId === requestRef.current) setError(requestError.message);
      return null;
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [memoryStatus, deferredSearch]);

  useEffect(() => { fetchMemories(""); }, [fetchMemories]);
  useEffect(() => { fetchMemoryUsage(); }, [fetchMemoryUsage]);

  const resetPage = () => {
    detailRequestRef.current += 1;
    setSelectedMemoryId(null);
    setSelectedMemory(null);
    setEditing(false);
    setConfirmingDelete(false);
    setMutationError(null);
    setPageCursors([""]);
    setPageIndex(0);
    setNextCursor(null);
  };

  const showNextPage = () => {
    if (!nextCursor || loading) return;
    const nextIndex = pageIndex + 1;
    setPageCursors((current) => [...current.slice(0, nextIndex), nextCursor]);
    setPageIndex(nextIndex);
    detailRequestRef.current += 1;
    setSelectedMemoryId(null);
    setSelectedMemory(null);
    fetchMemories(nextCursor);
  };

  const showPreviousPage = () => {
    if (pageIndex === 0 || loading) return;
    const previousIndex = pageIndex - 1;
    setPageIndex(previousIndex);
    detailRequestRef.current += 1;
    setSelectedMemoryId(null);
    setSelectedMemory(null);
    fetchMemories(pageCursors[previousIndex]);
  };

  const viewMemory = async (memoryId) => {
    if (selectedMemoryId === memoryId) {
      detailRequestRef.current += 1;
      setSelectedMemoryId(null);
      setSelectedMemory(null);
      setEditing(false);
      setConfirmingDelete(false);
      setMutationError(null);
      return;
    }

    const requestId = ++detailRequestRef.current;
    setSelectedMemoryId(memoryId);
    setSelectedMemory(null);
    setEditing(false);
    setConfirmingDelete(false);
    setMutationError(null);
    try {
      const detail = await tkadlecApi.getMemory(memoryId);
      if (requestId === detailRequestRef.current) setSelectedMemory(detail);
    } catch (requestError) {
      if (requestId === detailRequestRef.current) {
        setError(requestError.message);
        setSelectedMemoryId(null);
      }
    }
  };

  const startEditing = () => {
    const memory = selectedMemory?.memory;
    if (!memory) return;
    setEditForm({
      content: memory.content || "",
      type: memory.type || "other",
      retention: memory.retention || "temporary",
      confidence: Number(memory.confidence ?? 0.5),
      validUntil: memory.validUntil || "",
      reviewAfter: memory.reviewAfter || "",
    });
    setMutationError(null);
    setMutationMessage(null);
    setConfirmingDelete(false);
    setEditing(true);
  };

  const saveMemory = async (event) => {
    event.preventDefault();
    if (!editForm || !selectedMemory?.memory) return;
    setMutationLoading(true);
    setMutationError(null);
    setMutationMessage(null);
    try {
      const detail = await tkadlecApi.updateMemory(selectedMemory.memory.memoryId, {
        expectedVersion: selectedMemory.memory.version,
        ...editForm,
        confidence: Number(editForm.confidence),
        validUntil: editForm.retention === "permanent" ? null : editForm.validUntil || null,
        reviewAfter: editForm.retention === "permanent" ? null : editForm.reviewAfter || null,
      });
      setSelectedMemory(detail);
      setMemories((current) => current.map((memory) => (
        memory.memoryId === detail.memory.memoryId ? detail.memory : memory
      )));
      setEditing(false);
      setMutationMessage("Memory saved.");
      await fetchMemoryUsage();
      const refreshed = await fetchMemories(pageCursors[pageIndex]);
      if (refreshed && !refreshed.some((memory) => memory.memoryId === detail.memory.memoryId)) {
        setSelectedMemoryId(null);
        setSelectedMemory(null);
      }
    } catch (requestError) {
      setMutationError(requestError.message);
    } finally {
      setMutationLoading(false);
    }
  };

  const deleteMemory = async () => {
    if (!selectedMemory?.memory) return;
    setMutationLoading(true);
    setMutationError(null);
    setMutationMessage(null);
    try {
      const result = await tkadlecApi.deleteMemory(
        selectedMemory.memory.memoryId,
        selectedMemory.memory.version,
      );
      if (memoryStatus === "active") {
        setMemories((current) => current.filter((memory) => memory.memoryId !== result.memory.memoryId));
        setSelectedMemoryId(null);
        setSelectedMemory(null);
      } else {
        setMemories((current) => current.map((memory) => (
          memory.memoryId === result.memory.memoryId ? result.memory : memory
        )));
        setSelectedMemory(result);
      }
      setEditing(false);
      setConfirmingDelete(false);
      setMutationMessage("Memory deleted and retained in the archive.");
      await Promise.all([fetchMemories(pageCursors[pageIndex]), fetchMemoryUsage()]);
    } catch (requestError) {
      setMutationError(requestError.message);
    } finally {
      setMutationLoading(false);
    }
  };

  return (
    <div className="tkadlec-panel">
      {error && <div className="tkadlec-error">{error}</div>}
      {mutationError && <div className="tkadlec-error" role="alert">{mutationError}</div>}
      {mutationMessage && <div className="tkadlec-success" role="status">{mutationMessage}</div>}
      <div className="memory-toolbar">
        <div><h3>Shadow Memories</h3><span className="shadow-label">PRODUCTION · Anna uses these memories</span></div>
        <label>Status<select value={memoryStatus} onChange={(event) => { resetPage(); setMemoryStatus(event.target.value); }}><option value="active">Active</option><option value="archived">Archived</option><option value="superseded">Superseded</option><option value="">All</option></select></label>
      </div>

      <section className="memory-usage" aria-labelledby="memory-usage-title">
        <div className="memory-usage-heading">
          <div>
            <h3 id="memory-usage-title">Anna Memory Injection</h3>
            <p>Current table preview using the same selection and formatting rules as anna-chat.</p>
          </div>
          <button className="btn-secondary" type="button" onClick={fetchMemoryUsage} disabled={usageLoading}>Refresh usage</button>
        </div>
        {usageLoading && !memoryUsage ? (
          <p className="memory-usage-state" role="status">Loading memory usage...</p>
        ) : usageError ? (
          <div className="tkadlec-error" role="alert">Memory usage unavailable: {usageError}</div>
        ) : memoryUsage && (
          <>
            <div className="memory-counts">
              <div><strong>{memoryUsage.counts.active}</strong><span>Active memories</span></div>
              <div><strong>{memoryUsage.counts.overall}</strong><span>All current records</span></div>
              <div><strong>{memoryUsage.counts.archived}</strong><span>Archived</span></div>
              <div><strong>{memoryUsage.counts.superseded}</strong><span>Superseded</span></div>
            </div>
            <div className="memory-limits">
              <div>
                <span>Candidate memories read</span>
                <strong>{memoryUsage.injection.candidateMemories} / {memoryUsage.configuration.candidateMemoryLimit} memories</strong>
                <progress value={memoryUsage.injection.candidateMemories} max={memoryUsage.configuration.candidateMemoryLimit} />
              </div>
              <div>
                <span>Memories in Anna's injected block</span>
                <strong>{memoryUsage.injection.injectedMemories} / {memoryUsage.configuration.injectedMemoryLimit} memories</strong>
                <progress value={memoryUsage.injection.injectedMemories} max={memoryUsage.configuration.injectedMemoryLimit} />
              </div>
              <div>
                <span>Injected block characters</span>
                <strong>{memoryUsage.injection.blockCharacters.toLocaleString()} / {memoryUsage.configuration.blockCharacterLimit.toLocaleString()} characters</strong>
                <progress value={memoryUsage.injection.blockCharacters} max={memoryUsage.configuration.blockCharacterLimit} />
              </div>
            </div>
            <p className="memory-usage-note">
              Eligibility requires active status, non-empty content, and at least {Math.round(memoryUsage.configuration.minimumConfidence * 100)}% confidence. The block is ordered by permanent retention, confidence, then update time. There is no configured or measured token limit here; characters are not tokens. Past-due <code>validUntil</code> values remain eligible until Tkadlec changes status.
            </p>
            {(memoryUsage.injection.omittedByCandidateLimit > 0 || memoryUsage.injection.omittedByMemoryLimit > 0 || memoryUsage.injection.omittedByCharacterLimit > 0) && (
              <p className="memory-usage-omissions">
                Omitted now: {memoryUsage.injection.omittedByCandidateLimit} beyond the candidate cap, {memoryUsage.injection.omittedByMemoryLimit} beyond the injected-memory cap, and {memoryUsage.injection.omittedByCharacterLimit} that did not fit the character cap.
              </p>
            )}
          </>
        )}
      </section>

      <div className="memory-semantic-search">
        <label htmlFor="memory-search">Search all memory fields</label>
        <div className="memory-semantic-search-control">
          <input
            id="memory-search"
            type="search"
            placeholder="Search content, type, retention, dates, model..."
            value={searchQuery}
            onChange={(event) => {
              resetPage();
              setSearchQuery(event.target.value);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="memory-search-clear"
              onClick={() => { resetPage(); setSearchQuery(""); }}
              title="Clear search"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="memory-results-state" aria-live="polite">{loading ? "Updating..." : memories.length === 0 ? "No memories match these filters." : `${memories.length} memories on this page · ${totalMatched} match current filters`}</div>
      <div className="memory-table-wrap">
        <table className="memory-table">
          <thead><tr><th>Memory</th><th>Type</th><th>Retention</th><th>Valid until</th><th>Review after</th><th>Confidence</th><th>Updated</th></tr></thead>
          <tbody>{memories.map((memory) => [
              <tr
                key={`${memory.memoryId}-summary`}
                className={selectedMemoryId === memory.memoryId ? "selected" : ""}
                onClick={() => viewMemory(memory.memoryId)}
                aria-expanded={selectedMemoryId === memory.memoryId}
              >
                <td data-label="Memory">{memory.content}</td><td data-label="Type">{memory.type || "other"}</td>
                <td data-label="Retention"><span className={`retention-badge ${memory.retention || "temporary"}`}>{memory.retention || "temporary"}</span></td>
                <td data-label="Valid until">{memory.validUntil || "Not set"}</td><td data-label="Review after">{memory.reviewAfter || "Not set"}</td>
                <td data-label="Confidence">{Math.round(Number(memory.confidence || 0) * 100)}%</td><td data-label="Updated">{memory.updatedAt ? new Date(memory.updatedAt).toLocaleDateString() : "-"}</td>
              </tr>,
              selectedMemoryId === memory.memoryId && (
                <tr key={`${memory.memoryId}-detail`} className="memory-detail-row">
                  <td className="memory-detail-cell" colSpan={7}>
                    {selectedMemory?.memory ? (
                      <div className="memory-detail">
                        <div className="memory-detail-header">
                          <h3>Memory Detail</h3>
                          <div className="memory-detail-actions">
                            {selectedMemory.memory.status === "active" && !editing && (
                              <>
                                <button className="btn-secondary" onClick={startEditing} disabled={mutationLoading}>Edit</button>
                                <button className="btn-danger" onClick={() => { setMutationError(null); setConfirmingDelete(true); }} disabled={mutationLoading}>Delete</button>
                              </>
                            )}
                            <button className="btn-secondary" onClick={() => viewMemory(memory.memoryId)} disabled={mutationLoading}>Close</button>
                          </div>
                        </div>
                        {editing ? (
                          <form className="memory-edit-form" onSubmit={saveMemory}>
                            <label className="memory-edit-content">Memory<textarea required maxLength={4000} value={editForm.content} onChange={(event) => setEditForm({ ...editForm, content: event.target.value })} /></label>
                            <label>Type<select value={editForm.type} onChange={(event) => setEditForm({ ...editForm, type: event.target.value })}>{["fact", "preference", "instruction", "relationship", "object", "event", "other"].map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
                            <label>Retention<select value={editForm.retention} onChange={(event) => setEditForm({ ...editForm, retention: event.target.value, validUntil: event.target.value === "permanent" ? "" : editForm.validUntil, reviewAfter: event.target.value === "permanent" ? "" : editForm.reviewAfter })}><option value="permanent">permanent</option><option value="temporary">temporary</option></select></label>
                            <label>Confidence<input type="number" min="0" max="1" step="0.01" required value={editForm.confidence} onChange={(event) => setEditForm({ ...editForm, confidence: event.target.value })} /></label>
                            <label>Valid until<input type="text" placeholder="ISO-8601 date/time" value={editForm.validUntil} disabled={editForm.retention === "permanent"} onChange={(event) => setEditForm({ ...editForm, validUntil: event.target.value })} /></label>
                            <label>Review after<input type="text" placeholder="ISO-8601 date/time" value={editForm.reviewAfter} disabled={editForm.retention === "permanent"} onChange={(event) => setEditForm({ ...editForm, reviewAfter: event.target.value })} /></label>
                            <div className="memory-edit-actions">
                              <button type="submit" className="btn-primary" disabled={mutationLoading}>{mutationLoading ? "Saving..." : "Save"}</button>
                              <button type="button" className="btn-secondary" onClick={() => { setEditing(false); setMutationError(null); }} disabled={mutationLoading}>Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <p>{selectedMemory.memory.content}</p>
                            <dl>
                              <dt>Status</dt><dd>{selectedMemory.memory.status || "active"}</dd>
                              <dt>Version</dt><dd>{selectedMemory.memory.version || 1}</dd>
                              <dt>Reason</dt><dd>{selectedMemory.memory.modelReason || "-"}</dd>
                              <dt>Model</dt><dd>{selectedMemory.memory.model || "-"}</dd>
                              <dt>Review after</dt><dd>{selectedMemory.memory.reviewAfter || "-"}</dd>
                              <dt>Sources</dt><dd>{(selectedMemory.memory.sourceRequestIds || []).join(", ") || "-"}</dd>
                              <dt>Revisions</dt><dd>{selectedMemory.revisions?.length || 0}</dd>
                            </dl>
                          </>
                        )}
                        {confirmingDelete && (
                          <div className="memory-delete-confirmation" role="alertdialog" aria-labelledby="memory-delete-title">
                            <strong id="memory-delete-title">Delete this memory?</strong>
                            <p>Anna will stop using it. Its current value and revision history will remain in the archive.</p>
                            <div className="memory-edit-actions">
                              <button className="btn-danger" onClick={deleteMemory} disabled={mutationLoading}>{mutationLoading ? "Deleting..." : "Delete memory"}</button>
                              <button className="btn-secondary" onClick={() => setConfirmingDelete(false)} disabled={mutationLoading}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="memory-detail-loading">Loading memory detail...</div>
                    )}
                  </td>
                </tr>
              )
          ])}</tbody>
        </table>
      </div>
      <div className="memory-pagination" aria-label="Memory pages">
        <button className="btn-secondary" onClick={showPreviousPage} disabled={loading || pageIndex === 0}>Previous page</button><span>Page {pageIndex + 1}</span><button className="btn-secondary" onClick={showNextPage} disabled={loading || !nextCursor}>Next page</button>
      </div>

      <div className="memory-jobs"><h3>Recent Extraction Jobs</h3>{memoryJobs.length === 0 ? <p className="empty-state">No extraction jobs yet.</p> : memoryJobs.map((job) => (
        <div className="memory-job" key={job.jobId}><span className={`job-status ${job.status}`}>{job.status}</span><span>{job.jobId}</span><time>{job.updatedAt ? new Date(job.updatedAt).toLocaleString() : "-"}</time></div>
      ))}</div>
    </div>
  );
}